import React, { Component, ReactElement } from 'react';
import set from 'lodash/set';
import get from 'lodash/get';
import noop from 'lodash/noop';

import airbrake from 'common/airbrake';
import { ToastType } from './ToastType';
import { ForgeToast, ForgeIcon, ForgeScaffold } from '@tylertech/forge-react';
import { IToastCore } from '@tylertech/forge';

import './styles.scss';

/**
 * Namespace that toasts are stored under in sessionStorage.
 * Exported for testing purposes.
 */
export const SESSIONSTORAGE_NAMESPACE = 'socrata:toastmaster';

/** Default timeout if none is supplied for a given toast */
const DEFAULT_TIMEOUT = 5000;

export type Toast = {
  // Required
  content: string | ReactElement;

  // Optional
  forgeOptions?: Partial<IToastCore>;
  hideId?: string;
  onDismiss?: () => void;
  showNotification?: boolean;
  timeout?: number;
  type?: ToastType;
  forceEscapeContent?: boolean; // forces Toast to NOT use dangerouslySetInnerHTML
};

/** This will grab all of the toasts out of sessionStorage and add some defaults */
const getToastsFromSessionStorage = () =>
  JSON.parse(window.sessionStorage.getItem(SESSIONSTORAGE_NAMESPACE) || '[]').map((toast: Toast) => ({
    ...toast,
    showNotification: true,
  }));

/**
 * Adds a toast to sessionStorage that will get picked up on the next page load
 * @param {object} toastProps Toast properties; Requires at least a "content" attribute containing the notification text.
 */
const showToastOnPageReload = (toastProps: Toast) => {
  const currentToasts = JSON.parse(window.sessionStorage.getItem(SESSIONSTORAGE_NAMESPACE) || '[]');
  currentToasts.push(toastProps);
  window.sessionStorage.setItem(SESSIONSTORAGE_NAMESPACE, JSON.stringify(currentToasts));
};

/**
 * Show a toast right now; this actually just calls window.socrata.showToast (shhh)
 * @param {object} toastProps Toast properties; Requires at least a "content" attribute containing the notification text.
 */
const showToastNow = (toastProps: Toast) => {
  const showToast = get(window, 'socrata.showToast');
  if (typeof showToast === 'function') {
    showToast(toastProps);
  } else {
    // If you see this error, make sure the Toastmaster component has been rendered on the page.
    // It's likely that your application isn't rendering a #toastmaster DIV for site-wide.js to
    // find.
    airbrake.notify({ error: 'window.socrata.showToast is not a function' });
  }
};

/**
 * If you set a 'hideId' on your toasts, you can manually dismiss it with this method.
 * Actually just calls window.socrata.hideToast
 * @param {string} hideId optional parameter that can be passed when showing a toast, used to discover which toast to hide
 */
export const hideToastById = (hideId: string) => {
  const hideToast = get(window, 'socrata.hideToast');
  if (typeof hideToast === 'function') {
    hideToast(hideId);
  } else {
    // If you see this error, make sure the Toastmaster component has been rendered on the page.
    // It's likely that your application isn't rendering a #toastmaster DIV for site-wide.js to
    // find.
    airbrake.notify({ error: 'window.socrata.hideToast is not a function' });
  }
};

/**
 * Concise helper functions
 */
export const showSuccessToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.SUCCESS, content });
export const showErrorToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.ERROR, content });
export const showDefaultToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.DEFAULT, content });
export const showWarningToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.WARNING, content });
export const showInfoToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.INFO, content });
export const showForgeInfoToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.INFO, content });
export const showForgeDefaultToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.DEFAULT, content });
export const showForgeErrorToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.ERROR, content });
export const showForgeSuccessToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.SUCCESS, content });
// does not time out and is closeable
export const showStickyForgeErrorToastNow = (content: string | ReactElement) =>
  showToastNow({ type: ToastType.ERROR, content, timeout: Infinity, forgeOptions: { dismissible: true } });

export const showSuccessToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.SUCCESS, content });
export const showErrorToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.ERROR, content });
export const showDefaultToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.DEFAULT, content });
export const showWarningToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.WARNING, content });
export const showInfoToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.INFO, content });
export const showForgeDefaultToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.DEFAULT, content });
export const showForgeErrorToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.ERROR, content });
export const showForgeSuccessToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.SUCCESS, content });
export const showForgeInfoToastOnPageReload = (content: string | ReactElement) =>
  showToastOnPageReload({ type: ToastType.INFO, content });

type Props = object;

interface State {
  toasts: Toast[];
}

/**
 * This is intended to only be rendered once on every page and handles getting toast notifications out of
 * sessionStorage and displaying them to the user.
 *
 * This will also add a window.socrata.showToast function that can be called to show a toast for a given amount of time.
 *
 * (This component is actually rendered in common/site_wide.js)
 */
class Toastmaster extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      // grab all the toasts out of sessionStorage
      toasts: getToastsFromSessionStorage(),
    };

    // remove the toasts from sessionStorage so they don't show on the next page load
    window.sessionStorage.removeItem(SESSIONSTORAGE_NAMESPACE);
  }

  componentDidMount() {
    // create the window.socrata.showToast function that adds a toast to this component's state
    set(window, 'socrata.showToast', (toast: Toast) => {
      // we need to find this later via indexOf,
      // so we have to put the same object into the state that we hide later
      const visibleToast = {
        ...toast,
        showNotification: true,
      };

      this.setState({
        toasts: [...this.state.toasts, visibleToast],
      });
    });

    set(window, 'socrata.hideToast', (hideId: string) => {
      const toasts = [...this.state.toasts];
      const toastsToHide = toasts.filter((t) => t.hideId === hideId);
      toastsToHide.map((t) => {
        toasts[toasts.indexOf(t)] = {
          ...t,
          showNotification: false,
        };
      });
      if (toastsToHide.length > 0) {
        this.setState({
          toasts,
        });
      }
    });
  }

  componentWillUnmount() {
    // "remove: the window.socrata.showToast function
    set(window, 'socrata.showToast', (toast: Toast) => {
      console.warn('Toastmaster is unmounted but got toast', toast);
    });
  }

  /**
   * Update this component's state to have the given toast's "showNotification" property set to false.
   *
   * Note that "indexOf" is used here which does strict equality comparison (===) so if the toast
   * being passed in is a different object than the one that was originally added, this will NOT work
   */
  hideToast = (toast: Toast) => {
    const toasts = [...this.state.toasts];

    toasts[toasts.indexOf(toast)] = {
      ...toast,
      showNotification: false,
    };

    this.setState({
      toasts,
    });
  };

  iconForType = (toastType: ToastType | undefined): ReactElement | null => {
    let iconName: string | null = null;

    switch (toastType) {
      case ToastType.SUCCESS:
        iconName = 'check';
        break;
      case ToastType.ERROR:
        iconName = 'error';
        break;
      default:
        break;
    }

    return iconName ? <ForgeIcon className="toast-icon" slot="left" name={iconName} /> : null;
  };

  render() {
    const { toasts } = this.state;
    const toastItems = toasts.map((toast, key) => {
      const message = toast.content;
      const { forceEscapeContent = false } = toast;
      const toastProps = {
        key,
        open: toast.showNotification === undefined ? true : toast.showNotification,
        placement: 'top',
        id: 'socrata-forge-toast',
        onDismiss: toast.onDismiss || noop,
        'on-forge-toast-close': () => this.hideToast(toast),
        theme: toast.type,

        // Default timeout will be used unless toast.timeout overrides it below
        duration: DEFAULT_TIMEOUT,

        // Add on any other properties from toast
        ...toast,

        // Add on any forge specific properties from toast
        ...toast.forgeOptions,

        // Override duration property with toast.timeout
        ...(toast.timeout && { duration: toast.timeout }),
      };

      return (
        // key is set inside toastProps
        // eslint-disable-next-line react/jsx-key
        <ForgeToast {...toastProps}>
          {/* We have to use ForgeScaffold instead of ForgeToolbar because ForgeToolbar forces a white background */}
          <ForgeScaffold className="message-container" no-divider>
            {this.iconForType(toast.type)}
            {
              // Some of our older translations contain HTML so we should account for that.
              // We test for that by checking if a closing HTML tag exists within the text.
              !forceEscapeContent && typeof message === 'string' && /<\/[A-Za-z]+>/gi.test(message) ? (
                <span className="toast-message" slot="body" dangerouslySetInnerHTML={{ __html: message }} />
              ) : (
                <span className="toast-message" slot="body">
                  {message}
                </span>
              )
            }
          </ForgeScaffold>
        </ForgeToast>
      );
    });

    return <div aria-live="assertive">{toastItems}</div>;
  }
}

export { showToastOnPageReload, showToastNow, Toastmaster, ToastType };
